/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { createContext, ReactNode, useContext, useLayoutEffect, useState } from "react";
import _merge from "lodash/merge";
import { v4 as uuidv4 } from "uuid";
import { apiPost } from "../../services/apiClient";
import { CookieConsent } from "../../types/CookieConsent";
import { getConsent as cCSGetConsent, hasConsent as cCSHasConsent } from "../../services/cookieConsentService";
import dayjs from "dayjs";

const CookieConsentContext = createContext<{
  consent: CookieConsent | null;
  saveConsent: (consent: Pick<CookieConsent, "consentType" | "config">) => void;
  denyAll: () => void;
  allowAll: () => void;
  showCookieBanner: boolean;
  hasConsent: (key: keyof NonNullable<CookieConsent["config"]>) => boolean;
}>({
  consent: null,
  saveConsent: () => {},
  denyAll: () => {},
  allowAll: () => {},
  showCookieBanner: false,
  hasConsent: () => false,
});

const useCookieConsent = () => {
  return useContext(CookieConsentContext);
};

const COOKIE_POLICY_UPDATED_AT = dayjs("2026-04-21T00:00:00+02:00");

function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent | null>(cCSGetConsent());
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const consentDateTime = consent?.timestamp ? dayjs.unix(consent.timestamp / 1000) : null;

  function saveConsent(consent: Pick<CookieConsent, "consentType" | "config"> | null) {
    const consentData = _merge({}, consent, {
      hash: uuidv4(),
      timestamp: new Date().getTime(),
    });

    setConsent(consentData);
    localStorage.setItem("consent", JSON.stringify(consentData));
    apiPost("/cookie_consents", {
      hash: consentData.hash,
      consentData: consentData,
    });
  }

  useLayoutEffect(() => {
    if (!consent || !consentDateTime) {
      setShowCookieBanner(true);
      return;
    }
    if ((consentDateTime.isValid() && consentDateTime.isBefore(dayjs().subtract(6, "months"))) || consentDateTime.isBefore(COOKIE_POLICY_UPDATED_AT)) {
      setShowCookieBanner(true);
      removeConsentEntry();
      return;
    }
  }, []);

  function removeConsentEntry() {
    saveConsent(null);
    localStorage.removeItem("consent");
  }

  function denyAll() {
    saveConsent({
      consentType: "deny",
    });
  }

  function allowAll() {
    saveConsent({
      consentType: "allow",
    });
  }

  const hasConsent = (key: keyof NonNullable<CookieConsent["config"]>) => cCSHasConsent(key, consent ?? undefined);

  return (
    <CookieConsentContext.Provider value={{ consent, saveConsent, denyAll, allowAll, showCookieBanner, hasConsent }}>{children}</CookieConsentContext.Provider>
  );
}

export { useCookieConsent, CookieConsentProvider };
